// Erreichbarkeit, DNS und Bypass aus dem Homelab-Protokoll (Topics `uptime.stand`,
// `dns.lage`, `bypass.lage`; docs/protokoll.md › „Erreichbarkeit, DNS und Bypass
// über das Protokoll“) in genau die Form, die die Seiten aus den REST-Antworten
// kennen (`GET /v2-api/uptime/status`, `/v2-api/dns/status`, `/v2-api/settings/
// local-api-access`).
//
// Rein, ohne Runen und ohne Laufzeit-Importe, damit node (Test
// lib/erreichbarkeit-protokoll.test.mjs) sie laden kann. Die Stände kommen
// skaliert an (`skaliere(…)`; hier ist nichts skaliert, nur die Zeiten sind
// Unix-Millisekunden); Gegenstück der Rechnung ist internal/protokoll/adapter/
// erreichbarkeit.go.
//
// Regel, mit der die REST-Antwort wiederkehrt (der Adapter sendet spiegelbildlich):
//   - Felder, die die REST-Antwort ohne `omitempty` führt, stehen immer: fehlt der
//     Wert im Stand, wird er ""/0/false;
//   - Felder mit `omitempty` (`since`, `last_probe`, `last_error`, `cert_expiry`,
//     `cert_days_left`, beim DNS `since` und `latency_ms`) stehen am Ergebnis nur,
//     wenn der Stand sie trägt -- nie als `undefined`;
//   - Zeiten schreibt der Browser als ISO-Text mit Millisekunden („…01.903Z“);
//     die REST-Antwort trägt Nanosekunden. Gleich lesbar (Date.parse), nur nicht
//     als Text vergleichbar -- darum vergleicht die Status-Seite per Date.parse.

import type { DnsHealth } from "./dns-health.svelte";
import type { UptimeTarget } from "./uptime-summary.svelte";
import type { BypassLage, DnsLage, UptimeStand } from "./protokoll/schema.gen";

/** Instanzname → Id der Zeile: „uptime.ab12cd34ef56ab78.stand“ → „ab12cd34ef56ab78“. Alles andere → undefined. */
export function uptimeIdAusInstanz(instanz: string): string | undefined {
  const t = instanz.split(".");
  if (t.length !== 3 || t[0] !== "uptime" || t[2] !== "stand" || !/^[0-9a-f]+$/.test(t[1])) return undefined;
  return t[1];
}

/** Eine Zeile mit der Stelle, an der sie in der REST-Antwort stand. */
export interface UptimeEintrag {
  position: number;
  ziel: UptimeTarget;
}

/** Position, die eine Zeile ohne bekannte Stelle bekommt (ans Ende). */
const POSITION_UNBEKANNT = 1_000_000;

/** Unix-Millisekunden → ISO-Text; fehlt/unlesbar → undefined. */
function zeit(ms: number | null | undefined): string | undefined {
  if (typeof ms !== "number" || !Number.isFinite(ms)) return undefined;
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/** Setzt `o[k]`, wenn der Wert da ist (auch 0/false). */
function wenn<T extends object, K extends keyof T>(o: T, k: K, v: T[K] | null | undefined): void {
  if (v != null) o[k] = v as T[K];
}

/** Die Zeile aus ihrem Stand, wie `GET /v2-api/uptime/status` sie führt. Ohne `entity` → undefined. */
export function uptimeEintragAus(st: UptimeStand): UptimeEintrag | undefined {
  if (!st.entity) return undefined;
  const z: UptimeTarget = {
    entity: st.entity,
    target_id: st.ziel_id ?? "",
    label: st.bezeichnung ?? "",
    vantage: st.blickwinkel ?? "",
    url: st.url ?? "",
    health: st.zustand ?? "",
    ok: st.ok ?? false,
    http_status: st.http_status ?? 0,
    latency_ms: st.latenz ?? 0,
    consecutive_fails: st.fehlversuche ?? 0,
    flap_count: st.wackler ?? 0,
  };
  wenn(z, "since", zeit(st.seit));
  wenn(z, "last_probe", zeit(st.geprueft));
  wenn(z, "last_error", st.fehler);
  wenn(z, "cert_expiry", zeit(st.zertifikat_bis));
  wenn(z, "cert_days_left", st.zertifikat_tage);
  return { position: st.position ?? POSITION_UNBEKANNT, ziel: z };
}

/** Die Einträge einer REST-Antwort: je Zeile ihr Index. */
export function uptimeEintraegeAus(ziele: readonly UptimeTarget[]): UptimeEintrag[] {
  return ziele.map((ziel, position) => ({ position, ziel }));
}

/** Die Reihenfolge der REST-Antwort: nach Stelle, bei gleicher nach `entity`. */
export function vergleicheUptime(a: UptimeEintrag, b: UptimeEintrag): number {
  if (a.position !== b.position) return a.position < b.position ? -1 : 1;
  return a.ziel.entity === b.ziel.entity ? 0 : a.ziel.entity < b.ziel.entity ? -1 : 1;
}

/** Wie die REST-Antwort zählt: `up` und `down` nach `health`, `total` alle. */
export function uptimeZahlen(ziele: readonly Pick<UptimeTarget, "health">[]): { up: number; down: number; total: number } {
  let up = 0;
  let down = 0;
  for (const z of ziele) {
    if (z.health === "up") up++;
    else if (z.health === "down") down++;
  }
  return { up, down, total: ziele.length };
}

/** Der DNS-Zustand aus `dns.lage`, wie `GET /v2-api/dns/status` ihn führt. */
export function dnsAusProtokoll(l: DnsLage): DnsHealth {
  const o: DnsHealth = {
    ok: l.ok ?? false,
    health: l.zustand ?? "unknown",
    server: l.server ?? "",
    mode: l.modus ?? (l.ok ? "adguard" : "fallback"),
    flap_count: l.wackler ?? 0,
  };
  wenn(o, "since", zeit(l.seit));
  wenn(o, "latency_ms", l.latenz);
  return o;
}

/** Die Rohfelder des Bypass (das, was der Browser zum Rechnen braucht). */
export interface BypassRoh {
  enabled: boolean;
  autoOffMin: number;
  /** RFC-3339-Stempel des Einschaltens; "" = aus */
  enabledAt: string;
}

/** Die Rohfelder aus `bypass.lage`. */
export function bypassAusProtokoll(l: BypassLage): BypassRoh {
  return { enabled: l.an ?? false, autoOffMin: l.auto_aus_min ?? 0, enabledAt: zeit(l.an_seit) ?? "" };
}

/** Die Rohfelder aus der REST-Antwort von `GET /v2-api/settings/local-api-access`. */
export function bypassAusRest(d: { enabled?: boolean; auto_off_min?: number; enabled_at?: string }): BypassRoh {
  return {
    enabled: !!d.enabled,
    autoOffMin: typeof d.auto_off_min === "number" ? d.auto_off_min : 0,
    enabledAt: typeof d.enabled_at === "string" ? d.enabled_at : "",
  };
}
